import React, { useState } from 'react';
import { api } from '../api';

/**
 * OTP Authentication Gate Component
 * Florida 720.317 Compliance - Two-factor authentication for voting
 * 
 * Flow: Send OTP → Verify Code → Get Voter Token → Enable Voting
 */
const OTPGate = ({ electionId, userEmail, onAuthenticated, isTest = false }) => {
  const [step, setStep] = useState('send'); // 'send', 'verify', 'authenticated'
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expiresIn, setExpiresIn] = useState(null);

  const sendOTP = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await api.post('/api/vote/otp/send', {
        email: userEmail,
        election_id: electionId
      });
      
      setSuccess(response.data.message);
      setExpiresIn(response.data.expires_in_seconds);
      setStep('verify');
      
      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send authentication code');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/api/vote/otp/verify', {
        email: userEmail,
        code: code,
        election_id: electionId
      });
      
      const voterToken = response.data.voter_token;
      setStep('authenticated');
      setSuccess('Authentication successful! You may now cast your ballot.');
      
      // Pass token to parent component
      onAuthenticated(voterToken);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid authentication code');
      setCode(''); // Clear code on error
    } finally {
      setLoading(false);
    }
  };

  const handleCodeInput = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError('');
  };

  const handleResend = () => {
    setCode('');
    setError('');
    setSuccess('');
    setStep('send');
  };

  return (
    <div className="max-w-md mx-auto">
      {/* Security Badge */}
      <div className="mb-6 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-3">
          <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {isTest ? 'Device Test Authentication' : 'Secure Voter Authentication'}
        </h3>
        <p className="text-sm text-gray-600">
          Florida Statute 720.317 Compliance
        </p>
      </div>

      {/* Information Card */}
      {step === 'send' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-blue-700">
                {isTest ? (
                  <>
                    <strong>Testing Your Device:</strong> This process verifies your email and device work correctly before the actual election.
                  </>
                ) : (
                  <>
                    <strong>Why authenticate?</strong> This two-factor system ensures election integrity while maintaining ballot anonymity.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Send OTP */}
      {step === 'send' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email on File
            </label>
            <input
              type="email"
              value={userEmail}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            />
            <p className="mt-2 text-xs text-gray-500">
              A 6-digit code will be sent to this email address
            </p>
          </div>

          <button
            onClick={sendOTP}
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending Code...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Authentication Code
              </>
            )}
          </button>
        </div>
      )}

      {/* Step 2: Verify Code */}
      {step === 'verify' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center mb-6">
            <svg className="mx-auto h-12 w-12 text-green-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-gray-600">
              Code sent to <strong>{userEmail}</strong>
            </p>
            {expiresIn && (
              <p className="text-xs text-gray-500 mt-1">
                Expires in {Math.floor(expiresIn / 60)} minutes
              </p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
              Enter 6-Digit Code
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={handleCodeInput}
              placeholder="000000"
              maxLength="6"
              className="w-full px-4 py-3 text-center text-2xl font-mono tracking-wider border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter' && code.length === 6) {
                  verifyOTP();
                }
              }}
            />
            <p className="mt-2 text-xs text-gray-500 text-center">
              Check your email for the 6-digit code
            </p>
          </div>

          <button
            onClick={verifyOTP}
            disabled={loading || code.length !== 6}
            className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center mb-3"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Verify Code
              </>
            )}
          </button>

          <button
            onClick={handleResend}
            disabled={loading}
            className="w-full text-indigo-600 py-2 px-4 rounded-lg font-medium hover:bg-indigo-50 transition-colors disabled:text-gray-400"
          >
            Didn't receive code? Resend
          </button>
        </div>
      )}

      {/* Step 3: Authenticated */}
      {step === 'authenticated' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <svg className="mx-auto h-16 w-16 text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-bold text-green-900 mb-2">
            ✓ Authentication Successful
          </h3>
          <p className="text-sm text-green-700">
            You are now authorized to cast your ballot.
            {isTest && ' This is a test - your vote will not count in any real election.'}
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Security Notice */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-yellow-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Security Notice</h3>
            <div className="mt-2 text-xs text-yellow-700">
              <ul className="list-disc pl-5 space-y-1">
                <li>Never share your authentication code</li>
                <li>Each code can only be used once</li>
                <li>Codes expire in 10 minutes</li>
                <li>Your ballot remains anonymous</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPGate;
